import { compact, defaults } from 'lodash';

import {
  ApplicationConfig,
  ApplicationConfigRaw,
  ApplicationPartialConfig,
  Partners,
} from '@lockton-apps/shared/interfaces';
import { isDevMode, isExperimental } from '@lockton-apps/shared/util/helpers';

// MAIN CONFIGS
import { default as ACCA } from './ACCA.json';
import { default as ACCA_ACCOUNTANT_LONG } from './ACCA_ACCOUNTANT_LONG.json';
import { default as ACCA_ACCOUNTANT_NEW } from './ACCA_ACCOUNTANT_NEW.json';
import { default as ACCA_ACCOUNTANT_SHORT } from './ACCA_ACCOUNTANT_SHORT.json';
import { default as ACCA_DEPRECATED } from './ACCA_DEPRECATED.json';
import { default as ACCA_IP } from './ACCA_IP.json';
import { default as CONSTRUCTION_DNC } from './CONSTRUCTION_DNC.json';
import { default as CONSTRUCTION_ENA } from './CONSTRUCTION_ENA.json';
import { default as ENW_ENDURANCE } from './ENW_ENDURANCE.json';
import { default as ENW_HDIOVER25 } from './ENW_HDI-OVER-25.json';
import { default as ENW_HDIUNDER25 } from './ENW_HDI-UNDER-25.json';
import { default as ENW_MAJOR } from './ENW_MAJOR.json';
import { default as ENW_MAJOR_SHORT } from './ENW_MAJOR_SHORT.json';
import { default as ENW_MT } from './ENW_MT.json';
import { default as ENW_QBE } from './ENW_QBE.json';
import { default as ENW_SOLICITORS } from './ENW_SOLICITORS.json';
import { default as ENW_SOLICITORS_HDI } from './ENW_SOLICITORS_HDI.json';
import { default as ENW_SOLICITORS_2025 } from './ENW_SOLICITORS_2025.json';
import { default as ENW_SP } from './ENW_SP.json';
import { default as ENW_TRAVELERS } from './ENW_TRAVELERS.json';
import { default as LSOS } from './LSOS.json';
import { default as NORWAY } from './NORWAY.json';
import { default as NORWAY_NB } from './NORWAY_NB.json';
import { default as NORWAY_PI } from './NORWAY_PI.json';
import { default as NORWAY_PI_NB } from './NORWAY_PI_NB.json';
import { default as PERFORMANCE } from './PERFORMANCE.json';
import { default as PNC } from './PNC.json';
import { default as REGULATORY_IB } from './REGULATORY_IB.json';
import { default as REGULATORY_IFA } from './REGULATORY_IFA.json';
import { default as REGULATORY_LLOYDS_BROKERS } from './REGULATORY_LLOYDS_BROKERS.json';
import { default as SURVEYORS_MMNONTSA } from './SURVEYORS_MM-NON-TSA.json';
import { default as SURVEYORS_MMTSA } from './SURVEYORS_MM-TSA.json';
import { default as SURVEYORS_RTL } from './SURVEYORS_RTL.json';

// COMMON PARTIALS
import { default as COMMON } from './_partials/_COMMON.json';

// PARTNER SPECIFIC PARTIALS
import { default as ACCA_COMMON } from './_partials/_ACCA_COMMON.json';
import { default as ACCA_SHARED } from './_partials/_ACCA_SHARED.json';
import { default as CONSTRUCTION_QUESTIONNAIRE } from './_partials/_CONSTRUCTION_QUESTIONNAIRE.json';
import { default as ENW_COMMON } from './_partials/_ENW_COMMON.json';
import { default as REGULATORY_COMMON } from './_partials/_REGULATORY_COMMON.json';
import { default as SURVEYORS_QUESTIONNAIRE } from './_partials/_SURVEYORS_QUESTIONNAIRE.json';

// TESTING
import { default as _TEST_PROP_FORM_PARTIAL } from './_partials/_TEST_PROP_FORM_PARTIAL.json';
import { default as _TEST_PROP_FORM } from './_TEST_PROP_FORM_CONTROLS.json';

const _CONFIGS: Record<Partners, ApplicationConfigRaw[]> = {
  [Partners.LSOS]: [LSOS],
  [Partners.NORWAY]: [NORWAY, NORWAY_NB, NORWAY_PI, NORWAY_PI_NB],
  [Partners.NORWAY_PI]: [NORWAY, NORWAY_NB, NORWAY_PI, NORWAY_PI_NB],
  [Partners.ACCA]: [
    ACCA,
    ACCA_DEPRECATED,
    ACCA_IP,
    ACCA_ACCOUNTANT_SHORT,
    ACCA_ACCOUNTANT_LONG,
    ACCA_ACCOUNTANT_NEW,
  ],
  [Partners.PNC]: [PNC],
  [Partners.CONSTRUCTION]: [
    CONSTRUCTION_ENA,
    CONSTRUCTION_DNC,
    SURVEYORS_RTL,
    SURVEYORS_MMNONTSA,
    SURVEYORS_MMTSA,
  ],
  [Partners.ACCA_OVER]: [
    ACCA,
    ACCA_IP,
    ACCA_ACCOUNTANT_SHORT,
    ACCA_ACCOUNTANT_LONG,
    ACCA_ACCOUNTANT_NEW,
  ],
  [Partners.REGULATORY]: [
    REGULATORY_IFA,
    REGULATORY_IB,
    REGULATORY_LLOYDS_BROKERS,
  ],
  [Partners.ENW]: compact([
    ENW_ENDURANCE,
    ENW_TRAVELERS,
    ENW_HDIUNDER25,
    ENW_HDIOVER25,
    ENW_SOLICITORS,
    ENW_QBE,
    ENW_MT,
    ENW_SP,
    ENW_MAJOR,
    ENW_MAJOR_SHORT,
    ENW_SOLICITORS_HDI,
    ENW_SOLICITORS_2025,
  ]),
  [Partners.PERFORMANCE]: [PERFORMANCE],
} as unknown as Record<Partners, ApplicationConfig[]>;

const _PARTIALS: Record<string, ApplicationPartialConfig> = {
  COMMON,
  CONSTRUCTION_QUESTIONNAIRE,
  SURVEYORS_QUESTIONNAIRE,
  ENW_COMMON,
  REGULATORY_COMMON,
  ACCA_COMMON,
  ACCA_SHARED,
} as unknown as Record<string, ApplicationPartialConfig>;

export const CONFIGS: Record<Partners, ApplicationConfigRaw[]> =
  isExperimental() || isDevMode()
    ? defaults({ [Partners.TEST]: [_TEST_PROP_FORM] }, _CONFIGS)
    : _CONFIGS;

export const PARTIALS: Record<string, ApplicationPartialConfig> =
  isExperimental() || isDevMode()
    ? defaults({ _TEST_PROP_FORM_PARTIAL }, _PARTIALS)
    : _PARTIALS;
